import { Box, Button, Dialog } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { FONT_FAMILY } from "../../theme";

export interface ExpireGraceArtwork {
  src?: string;
  label?: string;
}

export interface ExpireGraceModalProps {
  open: boolean;
  trackCount: number;
  mbUsed: number;
  artworks?: ExpireGraceArtwork[];
  onSignup: () => void;
  onRemindLater: () => void;
  headline?: string;
}

const AMBER = "#E3A549";
const AMBER_INK = "#2a1d07";
const SURFACE = "#151718";
const BORDER = "#26292B";
const MUTED = "#889096";

const Thumbnail: React.FC<{ art?: ExpireGraceArtwork }> = ({ art }) => (
  <Box
    sx={{
      width: 64,
      height: 64,
      borderRadius: "10px",
      overflow: "hidden",
      border: `1px solid ${BORDER}`,
      background:
        "repeating-linear-gradient(135deg, #14171d, #14171d 11px, #181c23 11px, #181c23 22px)",
      display: "grid",
      placeItems: "center",
    }}
  >
    {art?.src ? (
      <Box component="img" src={art.src} alt={art.label ?? ""} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
    ) : (
      <Box
        sx={{
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
          fontSize: 9,
          color: "#5b626a",
          letterSpacing: "0.04em",
        }}
      >
        {art?.label ?? "art"}
      </Box>
    )}
  </Box>
);

const ExpireGraceModal: React.FC<ExpireGraceModalProps> = ({
  open,
  trackCount,
  mbUsed,
  artworks = [],
  onSignup,
  onRemindLater,
  headline = "Your guest session ends tomorrow",
}) => {
  const shown = artworks.slice(0, 3);
  const overflow = Math.max(0, artworks.length - shown.length);

  return (
    <Dialog
      open={open}
      onClose={onRemindLater}
      aria-labelledby="expire-grace-headline"
      PaperProps={{
        sx: {
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: "20px",
          boxShadow: "0 50px 130px -45px rgba(0,0,0,0.9)",
          width: 480,
          maxWidth: "calc(100% - 32px)",
          overflow: "hidden",
          fontFamily: FONT_FAMILY,
        },
      }}
    >
      <Box sx={{ padding: "36px 38px 30px", textAlign: "center" }}>
        <Box
          sx={{
            width: 76,
            height: 76,
            margin: "0 auto 20px",
            borderRadius: "50%",
            background: "rgba(227,165,73,0.15)",
            border: "1px solid rgba(227,165,73,0.36)",
            color: AMBER,
            display: "grid",
            placeItems: "center",
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 36 }} />
        </Box>
        <Box
          id="expire-grace-headline"
          sx={{
            color: "#FFFFFF",
            fontSize: 23,
            fontWeight: 700,
            letterSpacing: "-0.015em",
            fontFamily: FONT_FAMILY,
          }}
        >
          {headline}
        </Box>
        <Box
          sx={{
            color: MUTED,
            fontSize: 14.5,
            lineHeight: 1.55,
            margin: "11px auto 0",
            maxWidth: 400,
            fontFamily: FONT_FAMILY,
          }}
        >
          Sign up free to keep{" "}
          <Box component="b" sx={{ color: "#BCBEC0" }}>
            {trackCount} {trackCount === 1 ? "track" : "tracks"}
          </Box>{" "}
          and{" "}
          <Box component="b" sx={{ color: "#BCBEC0" }}>
            {mbUsed} MB
          </Box>{" "}
          of work. Once the session ends, they’re gone.
        </Box>
        {(shown.length > 0 || overflow > 0) && (
          <Box sx={{ display: "flex", gap: "10px", justifyContent: "center", margin: "22px 0 4px" }}>
            {shown.map((a, idx) => (
              <Thumbnail key={idx} art={a} />
            ))}
            {overflow > 0 && (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "10px",
                  border: `1px dashed ${BORDER}`,
                  display: "grid",
                  placeItems: "center",
                  color: MUTED,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                }}
              >
                +{overflow}
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "11px", marginTop: "26px" }}>
          <Button
            onClick={onSignup}
            sx={{
              background: AMBER,
              color: AMBER_INK,
              fontFamily: FONT_FAMILY,
              fontSize: 15,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              padding: "13px",
              "&:hover": { background: AMBER, filter: "brightness(1.06)" },
            }}
          >
            Sign up to keep everything
          </Button>
          <Button
            onClick={onRemindLater}
            sx={{
              background: "transparent",
              color: MUTED,
              fontFamily: FONT_FAMILY,
              fontSize: 15,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              padding: "13px",
              "&:hover": { background: "rgba(255,255,255,0.04)", color: "#FFFFFF" },
            }}
          >
            Remind me later
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ExpireGraceModal;
